import { A11yModule } from '@angular/cdk/a11y';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { MediaApi } from '@app/data/api';
import { MediaDto } from '@app/data/dto';
import { AppIcon } from '@app/shared/controls/app-icon/app-icon';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-image-lightbox',
  imports: [A11yModule, AppIcon, TranslatePipe],
  templateUrl: './image-lightbox.html',
  styleUrl: './image-lightbox.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageLightbox implements OnInit {
  private readonly mediaApi = inject(MediaApi);
  private readonly dialogRef = inject(DialogRef<void>, { optional: true });
  private readonly dialogData = inject<MediaDto>(DIALOG_DATA);

  protected readonly image = signal(this.dialogData);
  protected readonly imageUrl = signal<string | null>(null);
  protected readonly loading = signal(true);
  protected readonly hasError = signal(false);
  protected readonly downloading = signal(false);
  protected readonly altText = computed(() => this.image().fileName);

  ngOnInit(): void {
    void this.loadImage();
  }

  protected close(): void {
    this.dialogRef?.close();
  }

  protected async download(): Promise<void> {
    if (this.downloading()) {
      return;
    }

    this.downloading.set(true);

    try {
      await this.downloadMediaFile(this.image().mediaId, this.image().fileName);
    } finally {
      this.downloading.set(false);
    }
  }

  private async loadImage(): Promise<void> {
    this.loading.set(true);
    this.hasError.set(false);

    try {
      const media = await firstValueFrom(this.mediaApi.get(this.image().mediaId));
      this.image.set(media);
      this.imageUrl.set(media.mediaUrl ?? null);
      this.hasError.set(!media.mediaUrl);
    } catch {
      this.imageUrl.set(null);
      this.hasError.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  private async downloadMediaFile(mediaId: string, fallbackFileName: string): Promise<void> {
    const response = await firstValueFrom(this.mediaApi.downloadFile(mediaId));
    const blob = response.body;
    if (!blob) {
      return;
    }

    const fileName = this.getFileNameFromContentDisposition(response.headers.get('content-disposition'))
      ?? fallbackFileName;
    this.downloadBlob(blob, fileName);
  }

  private getFileNameFromContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8FileNameMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8FileNameMatch?.[1]) {
      return decodeURIComponent(utf8FileNameMatch[1].trim().replace(/^"|"$/g, ''));
    }

    const fileNameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return fileNameMatch?.[1]?.trim() || null;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    this.downloadUrl(url, fileName);
    globalThis.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private downloadUrl(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}
